import { Link } from "@tanstack/react-router";

import { useAuth, type UserRole } from "../../auth/AuthContext";
import { OpenQuotesCadenceInventory } from "../OpenQuotesCadenceInventory";
import { SearchBar } from "../layout/SearchBar";
import { GhostMark } from "../GhostMark";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useRecentActivity, useRiskQueue, type RecentActivityItem } from "../../queries";

function formatUsd(value: number) {
  return `$${Math.round(value).toLocaleString()}`;
}

function relativeTime(value: string) {
  const stamp = new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`).getTime();
  if (Number.isNaN(stamp)) return value;
  const diffMs = Date.now() - stamp;
  const minutes = Math.max(0, Math.round(diffMs / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function eventIcon(item: RecentActivityItem) {
  if (item.event_type === "message_sent") return "M";
  if (item.event_type === "reply_received") return "R";
  return "V";
}

export function canViewOpenQuotesCadence(role: UserRole | undefined) {
  return role === "owner" || role === "gm" || role === "sales";
}

export function QuotesContent() {
  const { user } = useAuth();
  const isSales = user?.role === "sales";
  const canViewCadence = canViewOpenQuotesCadence(user?.role);
  const riskQueue = useRiskQueue(!isSales);
  const recentActivity = useRecentActivity(10, !isSales);
  const riskItems = riskQueue.data?.items || [];
  const activityItems = recentActivity.data?.items || [];

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Quotes</Badge>
        <h1 className="mt-3 text-2xl font-semibold tracking-normal text-slate-950">Quote Control</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {isSales
            ? "Search a quote, inspect the full communication history, and send reviewed replies when needed."
            : "Search any quote or customer, inspect follow-up history, and keep Antonio focused on the quotes that need action."}
        </p>
      </div>

      {canViewCadence ? <OpenQuotesCadenceInventory /> : null}

      {!isSales ? (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <CardTitle>Needs Attention</CardTitle>
              <span className="text-xs text-slate-500">Call, reply, or review these first</span>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {riskQueue.isLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">Loading action list...</div>
            ) : riskQueue.isError ? (
              <div className="py-8 text-center text-sm text-red-600">Action list could not load.</div>
            ) : riskItems.length === 0 ? (
              <div className="rounded-md border border-emerald-100 bg-emerald-50 p-6 text-center text-sm text-emerald-800">
                No risk items. All quotes are tracked normally.
              </div>
            ) : (
              <table className="w-full min-w-[760px] text-sm">
                <thead className="text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="py-2">Priority</th>
                    <th className="py-2">Quote</th>
                    <th className="py-2">Customer</th>
                    <th className="py-2">Location</th>
                    <th className="py-2">Value</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">What to do</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {riskItems.map((row) => (
                    <tr key={row.quote_id}>
                      <td className="py-3">
                        <Badge variant={row.priority === "Critical" ? "red" : row.priority === "High" ? "amber" : "slate"}>
                          {row.priority}
                        </Badge>
                      </td>
                      <td className="py-3 font-medium text-slate-950">
                        <Link to="/quotes/$quoteId" params={{ quoteId: row.quote_id }} className="text-blue-700 hover:text-blue-900">
                          #{row.quote_id}
                        </Link>
                      </td>
                      <td className="py-3 text-slate-700">{row.customer_name}<GhostMark ghosted={row.ghosted} /></td>
                      <td className="py-3 text-slate-500">{row.location || "-"}</td>
                      <td className="py-3 text-slate-700">{formatUsd(row.value || 0)}</td>
                      <td className="py-3"><Badge variant="slate">{row.status}</Badge></td>
                      <td className="py-3 text-slate-700">{row.next_action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className={isSales ? "grid gap-5" : "grid gap-5 xl:grid-cols-[1fr_320px]"}>
        <Card>
          <CardHeader>
            <CardTitle>{isSales ? "Find a quote" : "Find a customer"}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-slate-500">
              {isSales
                ? "Search by quote ID, name, phone, or email. Results open Quote 360."
                : "Search by quote ID, name, phone, or email."}
            </p>
            <SearchBar />
          </CardContent>
        </Card>

        {!isSales ? (
          <Card>
            <CardHeader>
              <CardTitle>What changed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentActivity.isLoading ? (
                <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">Loading activity...</div>
              ) : activityItems.length === 0 ? (
                <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">No recent activity in the last 7 days.</div>
              ) : activityItems.map((item, index) => (
                <ActivityRow key={`${item.event_type}-${item.quote_id || index}-${item.occurred_at}`} item={item} />
              ))}
              <Link to="/ops" className="block">
                <Button variant="secondary" className="w-full">
                  Import data (5515 / API)
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </section>
  );
}

function ActivityRow({ item }: { item: RecentActivityItem }) {
  const isCustomerMessage = item.event_type === "reply_received";
  const body = (
    <div className="flex gap-3 rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-white text-[11px] font-semibold text-slate-700">
        {eventIcon(item)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-slate-900">{item.title}</span>
          <span className="shrink-0 text-slate-400">{relativeTime(item.occurred_at)}</span>
        </div>
        <div className="mt-1 truncate text-slate-500">
          {item.customer_name ? <>{item.customer_name}<GhostMark ghosted={item.ghosted} />{" - "}</> : ""}
          <span className={isCustomerMessage ? "italic" : undefined}>{item.detail || "No detail"}</span>
        </div>
      </div>
    </div>
  );

  if (!item.quote_id) return body;
  return (
    <Link to="/quotes/$quoteId" params={{ quoteId: item.quote_id }} className="block hover:opacity-90">
      {body}
    </Link>
  );
}
